import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  patchItem,
  updateProcurementResult,
  deleteProcurementResult,
} from '../../../shared/api/requestItems.api'
import type { SourceType } from '../../../shared/api/types'
import {
  convertQuoteToMatrixField,
  PRICE_FIELD_BY_MATRIX,
  type MatrixRates,
} from '../../requests/utils/convertQuoteToMatrixField'
import { AutoTextarea } from '../../../shared/ui/AutoTextarea'
import { formatShortDate } from '../../../shared/utils/formatDate'
import { AddResultForm, SOURCE_LABELS } from './AddResultForm'
import { DeleteQuoteDialog } from './DeleteQuoteDialog'
import { SupplierDuplicateHint } from '../../suppliers/components/SupplierDuplicateHint'

interface Result {
  id: string
  supplierName: string
  // null — квота-«заготовка» без ответа поставщика; 0 — «позиция отсутствует».
  price: number | null
  currency: string | null
  sourceType: SourceType
  note: string | null
  createdAt: string
  creator?: { id: string; name?: string | null; email: string } | null
  createdBy?: string
}

interface QuotesTableProps {
  results: Result[]
  itemId: string
  itemMatrix?: string | null // матрица позиции — дефолт для формы добавления квоты
  role: string | undefined
  userId: string | undefined
  /** Курсы валют. null — справочник не загружен: выбор квоты недоступен,
   *  иначе в позицию уехала бы цена по выдуманному курсу. */
  rates: MatrixRates | null
  selectedByMatrix: Record<string, string>
  setSelectedByMatrix: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isSaving: boolean
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>
  focusQuoteId?: string | null
  commentQuoteId?: string | null
  onSelectCommentQuote?: (quoteId: string | null) => void
}

export function QuotesTable({
  results,
  itemId,
  itemMatrix,
  role,
  userId,
  rates,
  selectedByMatrix,
  setSelectedByMatrix,
  isSaving,
  setIsSaving,
  focusQuoteId,
  commentQuoteId,
  onSelectCommentQuote,
}: QuotesTableProps) {
  const queryClient = useQueryClient()
  const [showResultForm, setShowResultForm] = useState(false)
  const [editingResultId, setEditingResultId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{
    supplierName: string
    price: string
    currency: string
    sourceType: SourceType
    note: string
  } | null>(null)
  const [editSaving, setEditSaving] = useState(false)
  // Квота, для которой открыт диалог подтверждения удаления.
  const [deletingResult, setDeletingResult] = useState<Result | null>(null)
  const [deleteSaving, setDeleteSaving] = useState(false)

  // РОЗ — полноценный закупщик: создаёт свои квоты как PROCUREMENT (API это
  // разрешал всегда, а кнопка на фронте РОЗ пропускала — баг, фикс 15.07).
  const isProcurement = role === 'PROCUREMENT' || role === 'ROS' || role === 'ADMIN'
  const canAddQuote = isProcurement || role === 'MANAGER' || role === 'ROP' || role === 'ASSISTANT'

  const confirmDelete = async () => {
    if (!deletingResult) return
    setDeleteSaving(true)
    try {
      await deleteProcurementResult(deletingResult.id)
      await queryClient.invalidateQueries({ queryKey: ['procurement-results', itemId] })
      await queryClient.invalidateQueries({ queryKey: ['requests'] })
      await queryClient.invalidateQueries({ queryKey: ['request-items-by-request'] })
      setDeletingResult(null)
    } finally {
      setDeleteSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-700">Квоты ({results.length})</h2>
        {canAddQuote && !showResultForm && (
          <button
            onClick={() => setShowResultForm(true)}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            + Добавить квоту
          </button>
        )}
      </div>

      {showResultForm && (
        <div className="p-4">
          <AddResultForm
            itemId={itemId}
            onDone={() => setShowResultForm(false)}
            userRole={role}
            defaultSourceType={itemMatrix}
          />
        </div>
      )}

      {results.length === 0 && !showResultForm ? (
        <p className="text-xs text-gray-400 px-4 py-3">Нет квот</p>
      ) : results.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-50 bg-gray-50/50">
                {(role === 'MANAGER' ||
                  role === 'ADMIN' ||
                  role === 'ROP' ||
                  role === 'ASSISTANT') && <th className="w-8 px-2 py-2" />}
                <th className="text-left px-3 py-2 font-medium text-gray-500">Поставщик</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Закупщик</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Дата</th>
                <th className="text-right px-3 py-2 font-medium text-gray-500">Цена</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Матрица</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Заметка</th>
                <th className="w-20 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const canEdit =
                  role === 'ADMIN' ||
                  ((role === 'PROCUREMENT' ||
                    role === 'ROS' ||
                    role === 'MANAGER' ||
                    role === 'ROP' ||
                    role === 'ASSISTANT') &&
                    r.creator?.id === userId)
                const isEditing = editingResultId === r.id

                if (isEditing && editForm) {
                  const saveEdit = async () => {
                    setEditSaving(true)
                    try {
                      await updateProcurementResult(r.id, {
                        supplierName: editForm.supplierName,
                        price: parseFloat(editForm.price),
                        currency: editForm.currency,
                        sourceType: editForm.sourceType,
                        note: editForm.note || null,
                      })
                      await queryClient.invalidateQueries({
                        queryKey: ['procurement-results', itemId],
                      })
                      await queryClient.invalidateQueries({ queryKey: ['requests'] })
                      await queryClient.invalidateQueries({
                        queryKey: ['request-items-by-request'],
                      })
                      setEditingResultId(null)
                      setEditForm(null)
                    } finally {
                      setEditSaving(false)
                    }
                  }
                  return (
                    <tr key={r.id} className="bg-amber-50/50 border-b border-gray-50">
                      <td
                        colSpan={
                          role === 'MANAGER' ||
                          role === 'ADMIN' ||
                          role === 'ROP' ||
                          role === 'ASSISTANT'
                            ? 8
                            : 7
                        }
                        className="p-2"
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                          <div className="sm:col-span-2">
                            <label className="block text-[10px] text-gray-500 mb-0.5">
                              Поставщик
                            </label>
                            <input
                              value={editForm.supplierName}
                              onChange={(e) =>
                                setEditForm({ ...editForm, supplierName: e.target.value })
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                            <SupplierDuplicateHint
                              name={editForm.supplierName}
                              onPick={(n) => setEditForm({ ...editForm, supplierName: n })}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">Цена</label>
                            <input
                              type="number"
                              value={editForm.price}
                              onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">Вал.</label>
                            <select
                              value={editForm.currency}
                              onChange={(e) =>
                                setEditForm({ ...editForm, currency: e.target.value })
                              }
                              className="w-full px-1 py-1 border border-gray-300 rounded text-xs bg-white"
                            >
                              <option>USD</option>
                              <option>EUR</option>
                              <option>CNY</option>
                              <option>RUB</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">
                              Матрица
                            </label>
                            <select
                              value={editForm.sourceType}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  sourceType: e.target.value as SourceType,
                                })
                              }
                              className="w-full px-1 py-1 border border-gray-300 rounded text-xs bg-white"
                            >
                              {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="sm:col-span-5">
                            <label className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                              <span>Заметка</span>
                              <span className="text-[10px] text-gray-400">
                                {editForm.note.length}/500
                              </span>
                            </label>
                            <AutoTextarea
                              value={editForm.note}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  note: e.target.value.slice(0, 500),
                                })
                              }
                              maxLength={500}
                              minRows={2}
                              className="w-full min-h-[2.25rem] px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div className="flex gap-1 sm:col-span-1">
                            <button
                              onClick={saveEdit}
                              disabled={editSaving || !editForm.supplierName || !editForm.price}
                              className="flex-1 px-2 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-50"
                            >
                              {editSaving ? '…' : 'OK'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingResultId(null)
                                setEditForm(null)
                              }}
                              className="px-2 py-1 text-gray-500 text-xs hover:text-gray-900"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                }

                return (
                  <tr
                    key={r.id}
                    data-focus-id={r.id}
                    className={`border-b border-gray-50 last:border-0 transition-colors ${
                      focusQuoteId === r.id
                        ? 'bg-brand-50'
                        : selectedByMatrix[r.sourceType] === r.id
                          ? 'bg-brand-50'
                          : 'hover:bg-gray-50/50'
                    }`}
                  >
                    {(role === 'MANAGER' ||
                      role === 'ADMIN' ||
                      role === 'ROP' ||
                      role === 'ASSISTANT') && (
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedByMatrix[r.sourceType] === r.id}
                          disabled={isSaving || !rates || r.price == null || Number(r.price) <= 0}
                          title={
                            r.price == null
                              ? 'Квота без цены — поставщик ещё не ответил'
                              : Number(r.price) <= 0
                                ? 'Поставщик ответил «позиции нет» — в КП не выбирается'
                                : !rates
                                  ? 'Курсы валют не загрузились — цену выбрать нельзя'
                                  : undefined
                          }
                          onChange={() => {
                            // Гейт на случай, если чекбокс всё же кликнули без
                            // курсов: цену в позицию писать не по чему.
                            if (!rates) return
                            const wasSelected = selectedByMatrix[r.sourceType] === r.id
                            setSelectedByMatrix((prev) => {
                              const next = { ...prev }
                              if (wasSelected) delete next[r.sourceType]
                              else next[r.sourceType] = r.id
                              return next
                            })
                            if (!wasSelected && r.price != null) {
                              const field = PRICE_FIELD_BY_MATRIX[r.sourceType] ?? 'targetPrice'
                              // Валюта квоты по умолчанию — USD (историческое
                              // поведение этой таблицы).
                              const finalPrice = convertQuoteToMatrixField(
                                r.price,
                                r.currency ?? 'USD',
                                r.sourceType,
                                rates,
                              )
                              setIsSaving(true)
                              void patchItem(itemId, {
                                [field]: finalPrice,
                              } as Parameters<typeof patchItem>[1])
                                .then((updated) => {
                                  queryClient.setQueryData(['request-items', itemId], updated)
                                  void queryClient.invalidateQueries({ queryKey: ['requests'] })
                                })
                                .finally(() => setIsSaving(false))
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-brand-600 cursor-pointer disabled:opacity-50"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium text-gray-900">{r.supplierName}</td>
                    <td className="px-3 py-2 text-gray-800">
                      {r.creator?.name || r.creator?.email || '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap font-mono">
                      {formatShortDate(r.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900 font-mono">
                      {r.price == null ? '—' : `${r.price} ${r.currency ?? ''}`}
                    </td>
                    <td className="px-3 py-2 text-gray-800">{SOURCE_LABELS[r.sourceType]}</td>
                    <td
                      className="px-3 py-2 text-gray-800 max-w-[240px] whitespace-normal break-words"
                      title={r.note ?? ''}
                    >
                      {r.note ?? '—'}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {onSelectCommentQuote && (
                          <button
                            onClick={() =>
                              onSelectCommentQuote(commentQuoteId === r.id ? null : r.id)
                            }
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                              commentQuoteId === r.id
                                ? 'bg-brand-100 text-brand-700'
                                : 'text-gray-500 hover:bg-brand-50 hover:text-brand-700'
                            }`}
                            title="Комментировать квоту"
                            type="button"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => {
                              setEditingResultId(r.id)
                              setEditForm({
                                supplierName: r.supplierName,
                                price: r.price == null ? '' : String(r.price),
                                currency: r.currency ?? 'USD',
                                sourceType: r.sourceType,
                                note: r.note ?? '',
                              })
                            }}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-700 hover:bg-brand-50 transition-colors"
                            title="Изменить квоту"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                          </button>
                        )}
                        {/* Крестик — удалить квоту. Видно автору квоты и ADMIN
                            (то же правило, что для редактирования). Бэк проверяет
                            права ещё раз в deleteResult. */}
                        {canEdit && (
                          <button
                            onClick={() => setDeletingResult(r)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Удалить квоту"
                            type="button"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {deletingResult && (
        <DeleteQuoteDialog
          quote={deletingResult}
          saving={deleteSaving}
          onCancel={() => setDeletingResult(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}
